import useResizeObserver from "@react-hook/resize-observer";
import { sortBy } from "lodash";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled, { css } from "styled-components";

import { partitionBy, reactKeyFromIDSK } from "@examspark/common";
import {
  NoResults,
  useQueryGetCoursesBySubject,
  useQuerySearchCourses,
  useStickyState,
} from "@examspark/common-app";
import {
  RouterLink,
  Tabs,
  Text,
  ToggleButtonGroup,
  Tooltip,
  VSpace,
} from "@examspark/common-ui";
import { IconRedirectLine } from "@examspark/common-ui/dist/components/icons";
import { SearchableResourceSortableFields } from "@examspark/types";

import Loading from "../common/Loading";
import SubjectIcon from "../common/SubjectIcon";

import { defaultBrowseFiltersBySubject } from "./Dashboard";

const Styled = styled.div`
  border-radius: 0 0 1rem 1rem;
  border: solid 1px hsl(0, 0%, 90%, 1);
`;
const StyledHeader = styled.div`
  background-color: #f7f7f7;
`;
const StyledBody = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  /* box-shadow: inset 0 0 0.3rem rgba(0, 0, 0, 0.1); */
`;

const TabIcon = styled.div`
  position: relative;
  margin: 0 auto;
  display: inline-block;
  padding: 1rem 1.5rem;
  border-radius: 0.5rem;
  transition: all 0.2s ease-in-out;

  img {
    display: block;
  }
`;

const SectionTitle = styled.div`
  color: #666;
  font-size: 1.3rem;
  border-bottom: solid 1px #ddd;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
`;

const StyledTabs = styled.div`
  button {
    height: auto;
    padding: 0.8rem;
  }
`;

const StyledLink = styled.div`
  padding: 0.5rem;
  border-top: solid 1px #ddd;
  display: flex;
  gap: 1rem;

  & > a {
    flex: 1;
    padding: 0.8rem 0;
    background-color: #f7f7f7;
    border-radius: 0.5rem;
    display: flex;
    gap: 1rem;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background-color 0.2s ease-in-out;

    &:hover {
      background-color: #f2f2f2;
    }
  }
`;

const StyledTabItem = styled(Tabs.TabItem)`
  /* border-left: solid 1px #ddd; */
  font-weight: normal;

  /* &:first-child {
    border-left: none;
  } */

  &:hover {
    ${TabIcon} {
      background-color: #ddd;
    }
  }
`;

const GridItemBadge = styled.div`
  --size: 4.6rem;
  width: var(--size);
  height: var(--size);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.6rem;
  color: #3e8db5;
  /* background-color: #3e8db5; */
  border-radius: 100%;
  transition: all 0.2s;

  border: solid 2px #3e8db5;
`;
const GridItemLabel = styled.div`
  flex: 1;
`;
interface GridItemProps {
  disabled?: boolean;
}

type GridProps = {
  rowCount?: number;
  colCount?: number;
};

const CoursesGrid = styled.div<GridProps>`
  display: grid;
  gap: 1rem;
  grid-auto-flow: column;
  grid-template-rows: repeat(${(props) => props.rowCount}, auto);
  grid-template-columns: repeat(${(props) => props.colCount}, 1fr);
`;

const GridItem = styled.div<GridItemProps>`
  border-radius: 0.5rem;
  background-color: #f2f2f2;
  /* border: solid 1px #f2f2f2; */
  padding: 1rem;
  color: #333;
  cursor: pointer;
  transition: all 0.2s;
  height: 5.5em;
  line-height: 1.5em;
  box-sizing: border-box;
  gap: 1rem;
  align-items: center;

  display: flex;
  color: hsl(250, 48%, 53%, 1);

  ${(props) =>
    props.disabled &&
    `
    opacity: 0.6;
    cursor: not-allowed;
  `}

  ${(props) =>
    !props.disabled &&
    css`
      &:hover {
        background-color: #e4e4ed;

        ${GridItemLabel} {
          text-decoration: underline;
        }
        ${GridItemBadge} {
          background-color: #3e8db5;
          color: #fff;
        }
      }
    `}

  &:hover {
  }
`;

const StyledLanguageToolbar = styled.div`
  & > div {
    max-width: 50%;
    margin: 0 auto;

    button {
      flex: 1;
      justify-content: center;
    }
  }
`;

const DashboardBrowser = () => {
  type SubjectTab = "PK" | "ELA" | "SLA" | "MATH" | "SCI" | "SS";

  const navigate = useNavigate();
  const [language, setLanguage] = useState("EN");

  const [tab, setTab] = useStickyState<SubjectTab>(
    "dashboardBrowserTab",
    "ELA",
  );

  const ref = React.createRef<HTMLDivElement>();
  const [size, setSize] = useState(0);
  useResizeObserver(ref, (entry) => {
    setSize(entry.contentRect.width);
  });

  let colCount = 3;
  if (size < 750) colCount = 2;
  if (size < 500) colCount = 1;

  const queriesByTab = {
    MATH: useQueryGetCoursesBySubject("MATH"),
    ELA: useQueryGetCoursesBySubject("ELA"),
    SLA: useQueryGetCoursesBySubject("SLA"),
    SCI: useQueryGetCoursesBySubject("SCI"),
    SS: useQueryGetCoursesBySubject("SS"),
    PK: useQuerySearchCourses({
      grade: "GRADE_PK",
      sort: [
        { field: SearchableResourceSortableFields.Sequence },
        { field: SearchableResourceSortableFields.Name },
      ],
    }),
  };

  const { data } = queriesByTab[tab];

  const partitionedCourses = partitionBy(data ?? [], (item) => {
    if (item.bankId?.match(/tcr/)) return "tcr";
    if (item.bankId?.match(/bluebonnet/)) return "bluebonnet";
    return "teksrs";
  });

  return (
    <VSpace size="1.5rem" ref={ref}>
      <Text.H1>Browse Curriculum By Subject</Text.H1>
      <Styled>
        <StyledHeader>
          <StyledTabs>
            <Tabs
              stretch
              value={tab}
              onChange={(value) => setTab(value as SubjectTab)}
            >
              <StyledTabItem value="PK" noTruncate>
                <Tooltip tip="Pre-Kindergarten Guidelines" placement="top">
                  <TabIcon>
                    <SubjectIcon
                      value="PK"
                      title="Pre-Kindergarten Guidelines"
                    />
                  </TabIcon>
                </Tooltip>
              </StyledTabItem>
              <StyledTabItem value="ELA" noTruncate>
                <Tooltip
                  tip="English-Language Arts and Reading"
                  placement="top"
                >
                  <TabIcon>
                    <SubjectIcon
                      value="ELA"
                      title="English-Language Arts and Reading"
                    />
                  </TabIcon>
                </Tooltip>
              </StyledTabItem>
              <StyledTabItem value="SLA" noTruncate>
                <Tooltip
                  tip="Spanish-Language Arts and Reading"
                  placement="top"
                >
                  <TabIcon>
                    <SubjectIcon
                      value="SLA"
                      title="Spanish-Language Arts and Reading"
                    />
                  </TabIcon>
                </Tooltip>
              </StyledTabItem>
              <StyledTabItem value="MATH" noTruncate>
                <Tooltip tip="Mathematics" placement="top">
                  <TabIcon>
                    <SubjectIcon value="MATH" title="Mathematics" />
                  </TabIcon>
                </Tooltip>
              </StyledTabItem>
              <StyledTabItem value="SCI" noTruncate>
                <Tooltip tip="Science" placement="top">
                  <TabIcon>
                    <SubjectIcon value="SCI" title="Science" />
                  </TabIcon>
                </Tooltip>
              </StyledTabItem>
              <StyledTabItem value="SS" noTruncate>
                <Tooltip tip="Social Studies" placement="top">
                  <TabIcon>
                    <SubjectIcon value="SS" title="Social Studies" />
                  </TabIcon>
                </Tooltip>
              </StyledTabItem>
            </Tabs>
          </StyledTabs>
        </StyledHeader>
        <StyledBody>
          <div style={{ flex: 1 }}>
            <VSpace size="2rem">
              <StyledLanguageToolbar>
                <div>
                  <ToggleButtonGroup
                    color="light-gray"
                    value={language}
                    onClick={(value) => {
                      setLanguage(value);
                    }}
                  >
                    <ToggleButtonGroup.Button value="EN">
                      English
                    </ToggleButtonGroup.Button>
                    <ToggleButtonGroup.Button value="ES">
                      Spanish
                    </ToggleButtonGroup.Button>
                  </ToggleButtonGroup>
                </div>
              </StyledLanguageToolbar>
              {data == null && <Loading>Loading Courses</Loading>}
              {data != null && data?.length === 0 && (
                <NoResults>
                  {tab === "PK" ? (
                    <div style={{ textAlign: "center" }}>
                      <h2>Pre-K is not available in this workspace (yet!)</h2>
                      <p>
                        Pre-K 4 instructional content is available in Go TCMPC
                        as an add-on to your district's subscription.
                      </p>
                      <p>
                        To request access, contact the TCMPC team at your
                        Education Service Center (ESC).
                      </p>
                    </div>
                  ) : (
                    <>We could not locate any courses for this subject.</>
                  )}
                </NoResults>
              )}

              {data != null && data?.length > 0 && (
                <>
                  {[
                    {
                      key: "teksrs",
                      label: "TEKS Resource System",
                    },
                    {
                      key: "tcr",
                      label: "Texas Curriculum Resources - CER",
                    },
                    {
                      key: "bluebonnet",
                      label: "Texas Curriculum Resources - Bluebonnet",
                    },
                  ].map((item) => {
                    const courses =
                      partitionedCourses[
                        item.key as keyof typeof partitionedCourses
                      ];
                    if (!courses?.length) return null;

                    const filteredCourses = courses.filter((course) => {
                      if (!course.language) {
                        if (language === "EN") return true;
                        return false;
                      }
                      return course?.language.includes(language);
                    });
                    const rowCount = Math.ceil(
                      filteredCourses.length / colCount,
                    );

                    return (
                      <div key={item.key}>
                        <SectionTitle>{item.label}</SectionTitle>
                        <CoursesGrid rowCount={rowCount} colCount={colCount}>
                          {sortBy(filteredCourses, "sequence").map((course) => {
                            return (
                              <GridItem
                                key={reactKeyFromIDSK(course)}
                                onClick={() => navigate(`/course/${course.id}`)}
                              >
                                <GridItemBadge>
                                  {course.numberLabel ?? "-"}
                                </GridItemBadge>
                                <GridItemLabel>{course?.name}</GridItemLabel>
                              </GridItem>
                            );
                          })}
                        </CoursesGrid>
                      </div>
                    );
                  })}
                </>
              )}
            </VSpace>
          </div>
        </StyledBody>
        <StyledLink>
          <RouterLink
            title="Browse All"
            to={`/browse?filter=${defaultBrowseFiltersBySubject[tab]}`}
          >
            <span>Browse all Content for This Subject Area</span>
            <IconRedirectLine width="16" />
          </RouterLink>
        </StyledLink>
      </Styled>
    </VSpace>
  );
};

export default DashboardBrowser;
